import React, { useState, useEffect } from "react";
import { Organization } from "../../../../../core/models/entities/Organization";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faArrowRight,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useWhiteLabelContext } from "../../../../../WhiteLabelContext";

interface CustomerSearchFilterProps {
  open?: boolean;
  initialValue?: string | number | boolean;
  onOpenToggle: (open: boolean) => void;
  onOptionSelect: (event: {
    value: string | number | boolean | undefined;
    key: string;
  }) => void;
  onRemoveFilter?: (key: string) => void;
  filterKey: string;
  defaultTitle: string;
  customers: Organization[];
}

export function CustomerSearchFilter({
  open = false,
  initialValue,
  onOpenToggle,
  onOptionSelect,
  onRemoveFilter,
  filterKey,
  defaultTitle,
  customers,
}: CustomerSearchFilterProps) {
  const [show, setShow] = useState(open);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [filteredResults, setFilteredResults] = useState<Organization[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Organization | null>(
    null
  );
  const { whiteLabel } = useWhiteLabelContext();

  useEffect(() => {
    setShow(open);
  }, [open]);

  // Initialize selected customer from initialValue
  useEffect(() => {
    if (initialValue && !selectedCustomer && customers) {
      const customer = customers.find((c) => c.id === Number(initialValue));
      if (customer) {
        setSelectedCustomer(customer);
      }
    } else if (!initialValue && selectedCustomer) {
      // Reset when initialValue is cleared (e.g., during filter reset)
      setSelectedCustomer(null);
      setInputValue("");
      setFilteredResults([]);
      setHasSearched(false);
    }
  }, [initialValue, selectedCustomer, customers]);

  // Filter customers based on search query
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = (customers || []).filter((customer) =>
        customer.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredResults(filtered);
      setHasSearched(true);
    } else {
      setFilteredResults([]);
      setHasSearched(false);
    }
  }, [searchQuery, customers]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setSearchQuery(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Search is automatic as user types
    }
  };

  const handleOpenClick = () => {
    const newShowState = !show;
    setShow(newShowState);
    onOpenToggle(newShowState);
  };

  const handleCustomerSelect = (customer?: Organization) => {
    setSelectedCustomer(customer || null);
    setShow(false);
    onOpenToggle(false);

    // Notify parent component of selection
    onOptionSelect({
      value: customer ? customer.id : undefined,
      key: filterKey,
    });

    // Clear search state
    setInputValue("");
    setFilteredResults([]);
    setHasSearched(false);
  };

  const handleClearSelection = () => {
    handleCustomerSelect(undefined);
  };

  // Clear results when input is empty
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredResults([]);
      setHasSearched(false);
    }
  }, [inputValue]);

  return (
    <div className="button-root w-48 px-0 relative !text-xs">
      <div className="relative">
        <button
          style={{
            color: selectedCustomer || show ? whiteLabel.colorHex : undefined,
          }}
          className={`button-root w-48 z-0 dropdown-button ${
            show ? "rounded-b-none" : ""
          }`}
          onClick={handleOpenClick}
        >
          <label className="max-w-full overflow-hidden truncate">
            {selectedCustomer ? selectedCustomer.name : defaultTitle}
          </label>
        </button>
        {selectedCustomer && onRemoveFilter && (
          <button
            onClick={() => onRemoveFilter(filterKey)}
            className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-500 transition-colors duration-200 z-10"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        )}
      </div>

      {show && (
        <div className="w-full z-10 absolute bg-gray-100 text-gray-600 border border-gray-200 rounded-md rounded-t-none border-t-0 text-xs text-center top-full">
          {/* Search input section */}
          <div className="p-2 border-b bg-white">
            <div className="border rounded-md border-gray-200 border-solid h-8 flex space-x-2 items-center pl-2 bg-white focus-within:border-gray-300 relative">
              <span>
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="text-gray-500 text-xs"
                />
              </span>
              <input
                className="border-none text-xs font-medium outline-none w-full pr-8"
                placeholder="Search for customer..."
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
              />
            </div>
          </div>

          {/* Search results */}
          {hasSearched && (
            <ul className="w-full max-h-72 overflow-y-auto">
              {filteredResults.map((customer) => (
                <li
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer)}
                  className="flex justify-center px-4 py-2 cursor-pointer text-gray-600 hover:text-white hover:bg-primary-100"
                >
                  {customer.name}
                </li>
              ))}
            </ul>
          )}

          {/* Show message when no search has been performed or clear filter option */}
          {selectedCustomer ? (
            <div className="p-2 text-xs">
              <button
                onClick={handleClearSelection}
                className="text-gray-500 hover:text-gray-700 italic cursor-pointer"
              >
                Clear filter
              </button>
            </div>
          ) : !hasSearched ? (
            <div className="p-2 text-xs">
              <span className="text-gray-500 italic">
                Start typing to search
              </span>{" "}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
